/**
 * HR Compliance tab (Admin → Tools → Audit): the weekly HR operations &
 * compliance report — view (server-rendered HTML), download (HTML + CSV zip),
 * and email enrollment. See documentation/HR/hr_ops_report_design.md.
 */
import { escapeHtml } from '../../src/lib/format.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const API = api.routes;
const adminAPI = api;
const showFlash = (msg, type) => notify.showFlash(msg, type);

function _el(id) { return document.getElementById(id); }

async function loadReport() {
  const body = _el('hrReportBody');
  if (!body) return;
  body.innerHTML = '<div class="muted" style="font-size:12px;">Loading…</div>';
  try {
    // The download endpoint returns fully-rendered HTML — use fetchText so the
    // raw string comes through (adminAPI.fetch coerces non-JSON bodies to {}).
    const html = await adminAPI.fetchText(API.adminHrReportDownload('html'), { method: 'GET', cache: 'no-store' });
    body.innerHTML = (typeof html === 'string' && html.trim()) ? html : '<div class="muted">No report.</div>';
  } catch (err) {
    body.innerHTML = `<div class="error">${escapeHtml((err && err.message) || 'Failed to load report')}</div>`;
  }
}

async function loadEnrollment() {
  const box = _el('hrReportEnroll');
  if (!box) return;
  box.innerHTML = '<div class="muted" style="font-size:12px;">Loading…</div>';
  try {
    const data = await adminAPI.fetch(API.adminHrReportSubscriptions, { method: 'GET' });
    const candidates = Array.isArray(data && data.candidates) ? data.candidates : [];
    if (!candidates.length) {
      box.innerHTML = '<div class="muted" style="font-size:12px;">No candidates found (root / HR employees / department managers with an email).</div>';
      return;
    }
    box.innerHTML = candidates.map((c) => `
      <label style="display:flex; align-items:center; gap:8px; font-size:13px; padding:2px 0;">
        <input type="checkbox" data-enroll-email="${escapeHtml(c.email)}" data-enroll-name="${escapeHtml(c.name)}" data-enroll-source="${escapeHtml(c.source)}" ${c.enrolled ? 'checked' : ''} />
        <span>${escapeHtml(c.name)} <span class="muted">&lt;${escapeHtml(c.email)}&gt; · ${escapeHtml(String(c.source || '').replace(/_/g, ' '))}</span></span>
      </label>`).join('');
  } catch (err) {
    box.innerHTML = `<div class="error">${escapeHtml((err && err.message) || 'Failed to load enrollment')}</div>`;
  }
}

function _download(format, ext) {
  const a = document.createElement('a');
  a.href = API.adminHrReportDownload(format);
  a.download = `hr_report.${ext}`;
  document.body.appendChild(a); a.click(); a.remove();
}

function wire() {
  if (!_el('toolsHrCompliancePanel')) return;
  const refresh = _el('hrReportRefreshBtn');
  if (refresh && !refresh.dataset.wired) { refresh.dataset.wired = '1'; refresh.addEventListener('click', () => { loadReport(); loadEnrollment(); }); }
  const dlHtml = _el('hrReportDownloadHtml');
  if (dlHtml && !dlHtml.dataset.wired) { dlHtml.dataset.wired = '1'; dlHtml.addEventListener('click', () => _download('html', 'html')); }
  const dlCsv = _el('hrReportDownloadCsv');
  if (dlCsv && !dlCsv.dataset.wired) { dlCsv.dataset.wired = '1'; dlCsv.addEventListener('click', () => _download('csv', 'zip')); }
  const enroll = _el('hrReportEnroll');
  if (enroll && !enroll.dataset.wired) {
    enroll.dataset.wired = '1';
    enroll.addEventListener('change', async (e) => {
      const cb = e.target.closest('[data-enroll-email]');
      if (!cb) return;
      try {
        await adminAPI.fetch(API.adminHrReportSubscriptions, {
          method: 'PUT',
          body: JSON.stringify({
            email: cb.getAttribute('data-enroll-email'),
            enrolled: cb.checked,
            name: cb.getAttribute('data-enroll-name'),
            source: cb.getAttribute('data-enroll-source'),
          }),
        });
        showFlash(cb.checked ? 'Enrolled' : 'Removed from list');
      } catch (err) {
        cb.checked = !cb.checked;
        showFlash((err && err.message) || 'Failed to update', 'error');
      }
    });
  }
}

function mount() {
  wire();
  loadReport();
  loadEnrollment();
}

export default { mount, loadReport };
